import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Eye, FileText, Loader2, Mail, Pencil, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { PdfPreviewDialog } from './PdfPreviewDialog';
import { usePdfPreview } from '../hooks/usePdfPreview';
import { fetchInvoicePdfUrl, previewCustomerChase } from '../services/api';
import { formatDate } from '../utils/date';

const FIELDS = ['subject', 'greeting_line', 'body_text', 'button_label', 'closer'];
const money = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const isEdited = (fields, defaults) => !!fields && FIELDS.some((k) => (fields[k] || '').trim() !== (defaults?.[k] || '').trim());

const InvoiceTabs = ({ items, selected, edits, onSelect }) => (
  <div className="space-y-1.5" data-testid="chase-composer-invoice-list">
    {items.map((it) => {
      const edited = isEdited(edits[it.uid], it.defaults);
      return (
        <button
          key={it.uid}
          type="button"
          onClick={() => onSelect(it.uid)}
          className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${
            selected === it.uid ? 'border-red-400 bg-red-50' : 'border-slate-200 hover:bg-slate-50'}`}
          data-testid={`chase-composer-tab-${it.uid}`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-sm text-slate-900 truncate">{it.displayNumber}</span>
            {edited && (
              <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-px rounded bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`chase-composer-edited-${it.uid}`}>
                Edited
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 flex justify-between gap-2">
            <span>Due {it.dueDate ? formatDate(it.dueDate) : '—'}</span>
            <span className="font-semibold text-slate-700">{money(it.amount)}</span>
          </div>
          <div className="text-[11px] text-slate-500 truncate">To {it.to}</div>
        </button>
      );
    })}
  </div>
);

const Field = ({ label, value, onChange, testid, multiline }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
    {multiline ? (
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-[34vh] min-h-[220px] text-sm leading-relaxed mt-1" placeholder="Blank line = new paragraph." data-testid={testid} />
    ) : (
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9 text-sm" data-testid={testid} />
    )}
  </div>
);

const Editor = ({ fields, onChange, onReset }) => (
  <div className="space-y-2.5" data-testid="chase-composer-editor">
    <Field label="Subject" value={fields.subject} onChange={(v) => onChange('subject', v)} testid="chase-composer-subject" />
    <Field label="Greeting" value={fields.greeting_line} onChange={(v) => onChange('greeting_line', v)} testid="chase-composer-greeting" />
    <Field label="Email body" multiline value={fields.body_text} onChange={(v) => onChange('body_text', v)} testid="chase-composer-body" />
    <div className="grid grid-cols-2 gap-2">
      <Field label="Button label" value={fields.button_label} onChange={(v) => onChange('button_label', v)} testid="chase-composer-button-label" />
      <Field label="Sign-off" value={fields.closer} onChange={(v) => onChange('closer', v)} testid="chase-composer-closer" />
    </div>
    <div className="flex items-center justify-between text-[10px] text-slate-500">
      <span>Lutrom logo, signature and the Review and pay button stay in place.</span>
      <button type="button" onClick={onReset} className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 underline underline-offset-2" data-testid="chase-composer-reset">
        <RotateCcw className="h-3 w-3" /> Reset to template
      </button>
    </div>
  </div>
);

const Attachment = ({ item, onOpen, busy }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 flex items-center gap-3" data-testid="chase-composer-attachment">
    <FileText className="h-5 w-5 text-red-600 shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium text-slate-900 truncate" data-testid="chase-composer-attachment-name">{item.attachment.filename}</div>
      <div className="text-[11px] text-slate-500">Original invoice PDF · attached to this email</div>
    </div>
    <Button type="button" variant="outline" size="sm" onClick={onOpen} disabled={busy} data-testid="chase-composer-attachment-open">
      <Eye className="h-3.5 w-3.5 mr-1.5" /> Open
    </Button>
  </div>
);

export const ChaseEmailComposer = ({ open, onClose, customerId, to, cc, throttle, sending, onSend }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [edits, setEdits] = useState({});
  const [mobileTab, setMobileTab] = useState('edit');
  const pdf = usePdfPreview();
  const renderTimer = useRef(null);
  const seq = useRef(0);

  const load = async (emails) => {
    const mine = ++seq.current;
    const res = await previewCustomerChase(customerId, { to, cc, emails });
    if (mine === seq.current) setData(res); // ignore stale renders
    return res;
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setEdits({});
    load({})
      .then((res) => setSelected(res.items[0]?.uid || null))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load chase emails'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Re-render the preview a moment after typing stops.
  useEffect(() => {
    if (!open || !data) return undefined;
    clearTimeout(renderTimer.current);
    renderTimer.current = setTimeout(() => { load(edits).catch(() => {}); }, 450);
    return () => clearTimeout(renderTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edits]);

  const item = useMemo(() => data?.items.find((i) => i.uid === selected), [data, selected]);
  const fields = (item && (edits[item.uid] || item.defaults)) || null;
  const editedUids = data ? data.items.filter((i) => isEdited(edits[i.uid], i.defaults)).map((i) => i.uid) : [];

  const change = (key, value) => setEdits((prev) => ({ ...prev, [item.uid]: { ...(prev[item.uid] || item.defaults), [key]: value } }));
  const reset = () => setEdits((prev) => { const n = { ...prev }; delete n[item.uid]; return n; });
  const send = () => {
    const payload = {};
    editedUids.forEach((uid) => { payload[uid] = edits[uid]; });
    onSend(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !sending) onClose(); }}>
      <DialogContent data-testid="chase-composer-dialog" className="max-w-7xl w-[calc(100vw-1rem)] max-h-[94vh]">
        <DialogHeader>
          <DialogTitle>Preview &amp; edit chase emails</DialogTitle>
          <DialogDescription>
            One email per overdue invoice. Edits are kept per invoice — nothing is sent until you press Send.
          </DialogDescription>
        </DialogHeader>
        {loading || !data ? (
          <div className="py-16 text-center text-slate-500 text-sm" data-testid="chase-composer-loading">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading chase emails…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)_minmax(0,1.1fr)] gap-4">
            <InvoiceTabs items={data.items} selected={selected} edits={edits} onSelect={setSelected} />
            {item && fields && (
              <>
                <div className="space-y-3 min-w-0">
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5 text-xs space-y-1" data-testid="chase-composer-headers">
                    <div className="flex gap-2"><span className="w-14 text-slate-500 uppercase tracking-wider">From</span><span className="truncate">{data.sender}</span></div>
                    <div className="flex gap-2"><span className="w-14 text-slate-500 uppercase tracking-wider">To</span><span className="truncate font-medium" data-testid="chase-composer-to">{item.to}</span></div>
                    {item.cc.length > 0 && <div className="flex gap-2"><span className="w-14 text-slate-500 uppercase tracking-wider">Cc</span><span className="truncate">{item.cc.join(', ')}</span></div>}
                  </div>
                  <div className="flex lg:hidden gap-2">
                    <Button size="sm" variant={mobileTab === 'edit' ? 'default' : 'outline'} onClick={() => setMobileTab('edit')} data-testid="chase-composer-mobile-edit"><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                    <Button size="sm" variant={mobileTab === 'preview' ? 'default' : 'outline'} onClick={() => setMobileTab('preview')} data-testid="chase-composer-mobile-preview"><Eye className="h-3.5 w-3.5 mr-1" />Preview</Button>
                  </div>
                  <div className={mobileTab === 'edit' ? '' : 'hidden lg:block'}>
                    <Editor fields={fields} onChange={change} onReset={reset} />
                  </div>
                </div>
                <div className={`space-y-2 min-w-0 ${mobileTab === 'preview' ? '' : 'hidden lg:block'}`}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Recipient view</div>
                  <div className="text-sm font-semibold text-slate-900 truncate" data-testid="chase-composer-preview-subject">{item.subject}</div>
                  <iframe srcDoc={item.html} title="Chase email preview" sandbox="" className="w-full h-[52vh] border border-slate-200 rounded-md bg-white" data-testid="chase-composer-preview-iframe" />
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pt-1">Attachments</div>
                  <Attachment item={item} busy={pdf.busy} onOpen={() => pdf.preview(() => fetchInvoicePdfUrl(item.uid))} />
                </div>
              </>
            )}
          </div>
        )}
        {throttle && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 flex items-start gap-2" data-testid="chase-composer-throttle">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {throttle.message}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="chase-composer-back">Back to recipients</Button>
          <Button
            onClick={send}
            disabled={sending || loading || !data?.items.length}
            className={throttle ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
            data-testid="chase-composer-send"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
            {sending ? 'Sending…' : `${throttle ? 'Send anyway' : 'Send'} ${data?.items.length || ''} chase email${data?.items.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
        <PdfPreviewDialog url={pdf.url} filename={pdf.filename} onClose={pdf.close} title="Attached invoice PDF" />
      </DialogContent>
    </Dialog>
  );
};

export default ChaseEmailComposer;
